import React, { memo, useEffect, ReactElement, RefObject } from 'react'
import classNames from 'classnames'
import { makeStyles, Typography } from 'transfix-ui/core'
import { useDropdownPosition } from 'utils/dropdownPosition'
import { useToggle } from 'utils/useToggle'

export interface ISelectBaseProps {
  id: string
  label?: React.ReactNode
  dataTest: string
  displayValue: string | ReactElement
  children: ReactElement | ReactElement[]
  onClear?: () => void
  onClose?: () => void
  applyFilter?: () => void
  hideOnSelect?: boolean
  disabled?: boolean
  required?: boolean
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  selectButtonType: string
  applied?: boolean
  stateCallback?(arg: boolean): void
  className?: string
  autocomplete?: boolean
  isAvatar?: boolean
  ariaLabel?: string
  /**
   * Text that shows above all the options in the dropdown
   * @optional
   * @example 'Ops Groups'
   */
  optionsLabel?: string
}

const useSelectBaseStyles = makeStyles(({ spacing }) => ({
  optionsLabel: {
    padding: spacing(1.5, 2.5, 0),
    display: 'block',
  },
}))

export const SelectBase = memo<ISelectBaseProps>(
  ({
    applied,
    applyFilter,
    ariaLabel,
    autocomplete = false,
    children,
    className,
    dataTest,
    disabled,
    displayValue,
    hideOnSelect,
    id,
    invalid,
    isAvatar,
    label,
    large,
    onClear,
    onClose,
    optionsLabel,
    required,
    selectButtonType,
    stateCallback,
    validationMessage,
  }) => {
    const styles = useSelectBaseStyles()

    const [expanded, toggle, wrapperRef] = useToggle<HTMLDivElement>(false, isExpanded => {
      if (!isExpanded && onClose) onClose()
    })
    const close = () => {
      if (expanded) toggle()
    }

    useEffect(() => {
      // hides dropdown if value changed in single select
      if (expanded && hideOnSelect && !autocomplete) toggle()
    }, [displayValue])

    useEffect(() => {
      // hides dropdown if control became disabled
      if (expanded && disabled) toggle()
    }, [disabled])

    useEffect(() => {
      // returns the state of the select listbox to parent
      if (stateCallback) {
        stateCallback(expanded)
      }
    }, [stateCallback, expanded])

    const applyFilterHandler = () => {
      if (applyFilter) applyFilter()
      toggle()
    }

    const [targetRef, style] = useDropdownPosition(
      expanded,
      close,
      undefined,
      false,
      false,
      true,
      autocomplete
    )
    const classes = classNames('select', className, {
      'select-large': large,
      expanded,
      disabled,
      invalid,
    })
    const validationText = validationMessage
    const isMultiSelect = onClear && applyFilter && stateCallback
    const isClearable = Boolean(onClear)

    const listClass = classNames('select-options', {
      'multi-options': isMultiSelect,
    })
    const ulClass = classNames({ 'pv2 ph1 filter-listbox-results': isMultiSelect })
    const buttonClass = classNames('tl', selectButtonType, {
      'filter-avatar': isAvatar,
      applied: applied || expanded,
      invalid,
    })

    const clearAllHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!onClear) return

      e.preventDefault()
      onClear()
    }

    return (
      <div className={classes} ref={wrapperRef} data-test={`${dataTest}-container`}>
        {label && (
          <legend data-test={`${dataTest}-label`} className={`label ${required ? 'required' : ''}`}>
            {label}
          </legend>
        )}
        <button
          aria-label={ariaLabel}
          className={buttonClass}
          type='button'
          aria-haspopup='listbox'
          id={id}
          onClick={toggle as () => void}
          data-test={dataTest}
          disabled={disabled}
          ref={targetRef as RefObject<HTMLButtonElement>}>
          {displayValue}
        </button>
        <div className={listClass} role='listbox' style={style}>
          {optionsLabel && (
            <Typography variant='caption' className={styles.optionsLabel} color='textSecondary'>
              {optionsLabel}
            </Typography>
          )}
          {autocomplete ? (
            <>
              {React.Children.map(children, (option: ReactElement) => {
                return (
                  <>
                    {option.props.onChange
                      ? React.cloneElement(option, {
                          onChange: (...args: any[]) => {
                            if (hideOnSelect && !!args[3]) {
                              close()
                            }
                            option.props.onChange(...args)
                          },
                        })
                      : option}
                  </>
                )
              })}
            </>
          ) : (
            <ul data-test={`${dataTest}-options`} className={ulClass}>
              {React.Children.map(children, (option: ReactElement) => (
                <li className={isMultiSelect ? 'mv1' : ''}>
                  {hideOnSelect && option.props.onSelect
                    ? React.cloneElement(option, {
                        onSelect: (...args: any[]) => {
                          close()
                          option.props.onSelect(...args)
                        },
                      })
                    : option}
                </li>
              ))}
              {isMultiSelect && (
                <div className='flex pt2 pb4 ph4'>
                  {isClearable && (
                    <button
                      className='btn-tertiary pa1 flex-grow-1'
                      onClick={clearAllHandler}
                      data-test={`${dataTest}-clear-all`}>
                      Clear all
                    </button>
                  )}
                  <button
                    className='btn-primary pa1 flex-grow-1 ml4 mt0'
                    onClick={applyFilterHandler}
                    data-test={`${dataTest}-apply`}>
                    Apply
                  </button>
                </div>
              )}
              {isClearable && !applyFilter && (
                <button
                  className='btn-text-link btn-full-width tl flex-grow-1'
                  onClick={clearAllHandler}
                  data-test={`${dataTest}-clear-all`}>
                  Clear all
                </button>
              )}
            </ul>
          )}
        </div>
        {validationText && <span className='validation-message'>{validationText}</span>}
      </div>
    )
  }
)
